import React, { useEffect, useState } from 'react';
import './OutwardEntry.css';
import axios from 'axios';
import { API_BASE_URL } from '../../config'; // <-- Use this import

const EditOutwardModal = ({
  editingEntry,
  handleChange,
  handleMaterialChange,
  addMaterial,
  removeMaterial,
  handleSave,
  handleCancel,
  error,
  success,
  setError
}) => {
  const [partySuggestions, setPartySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [materialSuggestions, setMaterialSuggestions] = useState([]);
  const [showMaterialSuggestions, setShowMaterialSuggestions] = useState(false);
  const [activeMaterialSuggestion, setActiveMaterialSuggestion] = useState(-1);
  const [materialSuggestionIndex, setMaterialSuggestionIndex] = useState(null);
  const [bills, setBills] = useState(editingEntry.bills && Array.isArray(editingEntry.bills) && editingEntry.bills.length > 0 ? editingEntry.bills : [{ bill_number: '', bill_amount: '' }]);

  useEffect(() => {
    if (editingEntry && editingEntry.bills && Array.isArray(editingEntry.bills)) {
      setBills(editingEntry.bills.length > 0 ? editingEntry.bills : [{ bill_number: '', bill_amount: '' }]);
    }
  }, [editingEntry]);

  useEffect(() => {
    // Only add a material row if there are no materials at all
    if (editingEntry && (!editingEntry.materials_list || editingEntry.materials_list.length === 0)) {
      handleChange('materials_list', [{ name: '', quantity: '', uom: '' }]);
    }
    // Do not add more than one row by default
    // eslint-disable-next-line
  }, [editingEntry]); // Remove addMaterial from dependencies

  const uomOptions = [
    'SqY', 'Sq.Ft', 'SqM', 'Cum', 'FTS', 'Kg', 
    'LTR', 'Mtr', 'Nos', 'PRS', 'SET','Box(es)','SM3','Sheet','Items'
  ];

  const purposeOptions = [
    'SALE',
    'RGP',
    'NRGP',
    'Inter Unit Transfer',
    'DC',
    'FOC',
    'Debit Note'
  ];

  const handleFieldChange = async (field, value) => {
    if (field === 'bill_number' && value) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/api/validation/bill/${value}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        if (response.data.exists) {
          setError('This bill number already exists in the system');
        } else {
          setError('');
        }
      } catch (err) {
        console.error('Error checking bill number:', err);
      }
    }
    handleChange(field, value);
  };

  const handlePartyNameChange = async (e) => {
    const value = e.target.value;
    handleFieldChange('party_name', value);
    if (value.length >= 2) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/api/party-names?q=${encodeURIComponent(value)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPartySuggestions(response.data);
        setShowSuggestions(true);
        setActiveSuggestion(-1);
      } catch (err) {
        setPartySuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setPartySuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (name) => {
    handleFieldChange('party_name', name);
    setShowSuggestions(false);
  };

  const handlePartyInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 100); // Delay to allow click
  };

  const handlePartyInputKeyDown = (e) => {
    if (!showSuggestions || partySuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setActiveSuggestion(prev => Math.min(prev + 1, partySuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      setActiveSuggestion(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeSuggestion >= 0) {
      handleSuggestionClick(partySuggestions[activeSuggestion]);
    }
  };

  const handleMaterialNameChange = async (index, value) => {
    handleMaterialChange(index, 'name', value);
    setMaterialSuggestionIndex(index); // Set focused index
    if (value.length >= 2) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/api/material-names?q=${encodeURIComponent(value)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMaterialSuggestions(response.data);
        setShowMaterialSuggestions(true);
        setActiveMaterialSuggestion(-1);
      } catch (err) {
        setMaterialSuggestions([]);
        setShowMaterialSuggestions(false);
      }
    } else {
      setMaterialSuggestions([]);
      setShowMaterialSuggestions(false);
    }
  };

  const handleMaterialSuggestionClick = (index, name) => {
    handleMaterialChange(index, 'name', name);
    setShowMaterialSuggestions(false);
    setMaterialSuggestionIndex(null);
  };

  const handleMaterialInputFocus = (index) => {
    setMaterialSuggestionIndex(index);
  };

  const handleMaterialInputBlur = () => {
    setTimeout(() => {
      setShowMaterialSuggestions(false);
      setMaterialSuggestionIndex(null);
    }, 100);
  };

  const handleMaterialInputKeyDown = (e) => {
    if (!showMaterialSuggestions || materialSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setActiveMaterialSuggestion(prev => Math.min(prev + 1, materialSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      setActiveMaterialSuggestion(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeMaterialSuggestion >= 0 && materialSuggestionIndex !== null) {
      handleMaterialSuggestionClick(materialSuggestionIndex, materialSuggestions[activeMaterialSuggestion]);
    }
  };

  const handleBillChange = (index, field, value) => {
    setBills(prevBills => {
      const newBills = [...prevBills];
      newBills[index] = { ...newBills[index], [field]: value };
      // propagate to parent with updated bills
      handleChange('bills', newBills);
      return newBills;
    });
  };

  const addBill = () => {
    setBills(prev => [...prev, { bill_number: '', bill_amount: '' }]);
  };

  const removeBill = (index) => {
    setBills(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <style>
        {`
          .material-suggestion-list {
            position: absolute;
            z-index: 1000;
            background: #fff;
            border: 1px solid #e0e7ef;
            border-radius: 10px;
            box-shadow: 0 4px 16px rgba(60,72,88,0.12);
            margin-top: 2px;
            width: 100%;
            max-height: 220px;
            overflow-y: auto;
            padding: 0;
            list-style: none;
            animation: fadeIn 0.18s;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-8px);}
            to { opacity: 1; transform: translateY(0);}
          }
          .material-suggestion-item {
            padding: 10px 18px;
            cursor: pointer;
            transition: background 0.15s, color 0.15s;
            font-size: 16px;
            color: #232946;
            display: flex;
            align-items: center;
            border-bottom: 1px solid #f3f4f6;
          }
          .material-suggestion-item:last-child {
            border-bottom: none;
          }
          .material-suggestion-item.active,
          .material-suggestion-item:hover {
            background: #e0e7ff;
            color: #3730a3;
          }
          .material-suggestion-item b {
            font-weight: 600;
            color: #6366f1;
          }
        `}
      </style>
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Edit Outward Entry</h2>
            <button className="close-button" onClick={handleCancel}>&times;</button>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="modal-body">
            <div className="info-grid">
              {/* Only show: purpose, party name */}
              <div className="info-item">
                <label>Purpose <span style={{color: 'black'}}>*</span></label>
                <select
                  value={editingEntry.purpose || ''}
                  onChange={(e) => handleFieldChange('purpose', e.target.value)}
                  required
                >
                  <option value="">Select Purpose</option>
                  {purposeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div
                className="info-item party-name-advanced"
                style={{ width: '80%', marginBottom: 16 }}
              >
                <label>
                  <i className="fas fa-building" style={{ marginRight: 6, color: '#6366f1' }}></i>
                  Party Name <span style={{color: 'black'}}>*</span>
                </label>
                <input
                  type="text"
                  value={editingEntry.party_name || ''}
                  onChange={handlePartyNameChange}
                  onBlur={handlePartyInputBlur}
                  onKeyDown={handlePartyInputKeyDown}
                  placeholder="Type to search or add party"
                  required
                  autoComplete="off"
                  className="party-name-input"
                  style={{ paddingLeft: '2rem' }}
                />
                {showSuggestions && partySuggestions.length > 0 && (
                  <ul className="party-suggestion-list">
                    {partySuggestions.map((name, idx) => (
                      <li
                        key={idx}
                        className={`party-suggestion-item${activeSuggestion === idx ? ' active' : ''}`}
                        onMouseDown={() => handleSuggestionClick(name)}
                      >
                        <i className="fas fa-user-tag" style={{ marginRight: 8, color: '#6366f1' }}></i>
                        <span>
                          {editingEntry.party_name
                            ? (
                              <>
                                {name.substring(0, editingEntry.party_name.length)}
                                <b>{name.substring(editingEntry.party_name.length)}</b>
                              </>
                            )
                            : name
                          }
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* --- New fields for Outward Weights and Slip Number --- */}
              <div className="info-item" style={{ width: '80%' }}>
                <label style={{ marginBottom: 4 }}>Gross Weight <span style={{color: 'black'}}>*</span></label>
                <input
                  type="number"
                  value={editingEntry.gross_weight || ''}
                  onChange={e => handleFieldChange('gross_weight', e.target.value)}
                  placeholder="Enter Gross Weight"
                  min="0"
                  step="0.01"
                  style={{ width: '100%' }}
                  required
                />
              </div>
              <div className="info-item" style={{ width: '80%', marginBottom: 16  }}>
                <label style={{ marginBottom: 4 }}>Tare Weight <span style={{color: 'black'}}>*</span></label>
                <input
                  type="number"
                  value={editingEntry.tare_weight || ''}
                  onChange={e => handleFieldChange('tare_weight', e.target.value)}
                  placeholder="Enter Tare Weight"
                  min="0"
                  step="0.01"
                  style={{ width: '100%' }}
                  required
                />
              </div>
              <div className="info-item" style={{ width: '80%' }}>
                <label style={{ marginBottom: 4 }}>Net Weight <span style={{color: 'black'}}>*</span></label>
                <input
                  type="number"
                  value={
                    editingEntry.net_weight !== undefined
                      ? editingEntry.net_weight
                      : (
                        (parseFloat(editingEntry.gross_weight) || 0) -
                        (parseFloat(editingEntry.tare_weight) || 0)
                      )
                  }
                  readOnly
                  placeholder="Net Weight"
                  style={{ width: '100%', background: '#f3f4f6' }}
                  required
                />
              </div>
              <div className="info-item" style={{ width: '80%' }}>
                <label style={{ marginBottom: 4 }}>Weight Slip Number <span style={{color: 'black'}}>*</span></label>
                <input
                  type="text"
                  value={editingEntry.weight_slip_number || ''}
                  onChange={e => handleFieldChange('weight_slip_number', e.target.value)}
                  placeholder="Enter Weight Slip Number"
                  style={{ width: '100%' }}
                  required
                />
              </div>
            </div>

            {/* Out Date and Remarks Section */}
            <div className="info-grid">
              <div className="info-item" style={{ width: '52%' }}>
                <label style={{ marginBottom: 4 }}>Out Date <span style={{color: 'black'}}>*</span></label>
                <input
                  type="text"
                  id="out_date"
                  name="out_date"
                  value={
                    editingEntry.out_date
                      ? editingEntry.out_date.slice(0, 10) // Only show YYYY-MM-DD
                      : ''
                  }
                  disabled
                  style={{
                    backgroundColor: '#e0e0e0',
                    color: '#555',
                    cursor: 'not-allowed',
                    userSelect: 'none'
                  }}
                  tabIndex={-1}
                  readOnly
                  required
                />
              </div>
              <div className="info-item" style={{ width: '100%' }}>
                <label>Remarks <span style={{color: 'black'}}>*</span></label>
                <textarea
                  value={editingEntry.remarks || ''}
                  onChange={(e) => handleFieldChange('remarks', e.target.value)}
                  placeholder="Enter remarks"
                  required
                />
              </div>
            </div>

            {/* --- Bills Section moved after Out Date and Remarks --- */}
            <div className="bills-section">
              <label style={{ fontWeight: 500, marginBottom: 8 }}>Bill Details</label>
              {bills.map((bill, idx) => (
                <div className="bill-row-horizontal" key={idx}>
                  <div className="form-group bill-field">
                    <label>Bill Number</label>
                    <input
                      type="text"
                      value={bill.bill_number}
                      onChange={e => handleBillChange(idx, 'bill_number', e.target.value)}
                      placeholder="Enter Bill Number"
                      style={{ width: 180 }}
                    />
                  </div>
                  <div className="form-group bill-field">
                    <label>Bill Amount</label>
                    <input
                      type="number"
                      value={bill.bill_amount}
                      onChange={e => handleBillChange(idx, 'bill_amount', e.target.value)}
                      placeholder="Enter Bill Amount"
                      style={{ width: 180 }}
                    />
                  </div>
                  {bills.length > 1 && (
                    <button type="button" className="remove-bill-btn" onClick={() => removeBill(idx)}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-bill-btn" onClick={addBill}>
                Add Bill
              </button>
            </div>
            {/* --- End Bills Section --- */}


            {/* Materials Section */}
            <div className="materials-section">
              <div className="materials-header">
                <h3>Materials <span style={{color: 'black'}}>*</span></h3>
                <button type="button" className="add-material-btn" onClick={addMaterial}>
                  Add Material
                </button>
              </div>
              <div className="materials-edit-list">
                {(editingEntry.materials_list || []).map((material, index) => (
                  <div key={index} className="material-edit-row" style={{ position: 'relative' }}>
                    <div className="material-edit-fields">
                      <div className="material-field" style={{ position: 'relative' }}>
                        <label>Material Name <span style={{color: 'black'}}>*</span></label>
                        <input
                          type="text"
                          value={material.name || ''}
                          onChange={(e) => handleMaterialNameChange(index, e.target.value)}
                          onFocus={() => handleMaterialInputFocus(index)}
                          onBlur={handleMaterialInputBlur}
                          onKeyDown={handleMaterialInputKeyDown}
                          placeholder="Enter material name"
                          required
                          autoComplete="off"
                        />
                        {showMaterialSuggestions && materialSuggestionIndex === index && materialSuggestions.length > 0 && (
                          <ul className="material-suggestion-list">
                            {materialSuggestions.map((name, idx) => (
                              <li
                                key={idx}
                                className={`material-suggestion-item${activeMaterialSuggestion === idx ? ' active' : ''}`}
                                onMouseDown={() => handleMaterialSuggestionClick(index, name)}
                              >
                                <span>
                                  {material.name
                                    ? (
                                      <>
                                        {name.substring(0, material.name.length)}
                                        <b>{name.substring(material.name.length)}</b>
                                      </>
                                    )
                                    : name
                                  }
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="material-field">
                        <label>Quantity <span style={{color: 'black'}}>*</span></label>
                        <input
                          type="number"
                          value={material.quantity || ''}
                          onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                          placeholder="Enter quantity"
                          required
                        />
                      </div>
                      <div className="material-field">
                        <label>UoM <span style={{color: 'black'}}>*</span></label>
                        <select
                          value={material.uom || ''}
                          onChange={(e) => handleMaterialChange(index, 'uom', e.target.value)}
                          required
                        >
                          <option value="">Select UoM</option>
                          {uomOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      className="remove-material-btn"
                      onClick={() => removeMaterial(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button type="button" className="save-btn" onClick={handleSave}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditOutwardModal;
